import * as stylex from "@stylexjs/stylex";
import { ArrowTop16 } from "icons/ArrowTop";
import Link from "next/link";
import { useRouter } from "next/router";
import { FormattedMessage } from "react-intl";
import { useRTL } from "../hooks";
import { useAppReducer } from "../hooks/useAppState";
import { locales } from "../locales";
import { breakpoints, colors, zIndex } from "../materials/consts.stylex";
import { gridCenteredMaxWidthAreas, layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { E, O, pipe } from "../prelude";
import { aliasFromRouter, findArticleAlias, RAlias } from "../routes";
import { LocaleLink } from "./links";
import { LogoIO } from "./logo";
import SecondaryBrandsBanner from "./secondary-brands-banner";
const stylexStyles = {
    root: {
        k9llMU: "xi217zw",
        kC13JO: "x1ips9pf",
        kWkggS: "xdutcdc",
        kMwMTN: "x1vc0dw",
        kLKAdn: "xyamay9",
        kGO01o: "x1l90r2v",
        kE3dHu: "xnm25rq x1b644cj",
        kpe85a: "xyfqnmn x11wpv5z",
        kVAEAm: "x1n2onr6",
        k1K539: "xsiyqvz",
        $$css: true
    },
    locales: {
        k1xSpc: "x1s85apg xjmiiu8",
        kGNEyG: "x6s0dn4",
        $$css: true
    },
    linkButton: {
        ksu8eU: "xng3xce",
        kogj98: "xhhrcr6",
        kmVPX3: "x1717udv",
        kzqmXN: "x14atkfc",
        kVQacm: "x1rea2x4",
        kWkggS: "xjbqb8w",
        khiITx: "x1e8rmrl",
        kTUp5R: "x1tea8nt",
        k3lkva: "x1lugfcp",
        $$css: true
    },
    linkButtonRtl: {
        kogj98: "xayb2vf",
        $$css: true
    },
    link: {
        kMwMTN: "x1heor9g",
        kP9fke: "xtvhhri",
        kkrTdU: "x1ypdohk",
        kVAEAm: "x1n2onr6",
        kSiTet: "xbyyjgo",
        kmkexE: "xbkm4cr",
        kKhh5M: "x1o7uuvo",
        kBCxwx: "x25t5g8",
        kPJHz1: "x784prv",
        k7shQP: "x9v5kkp",
        kZs9gx: "xtj0x4i",
        kiEn40: "x7s97pk",
        $$css: true
    },
    navLinkSpacingLtr: {
        koQZXg: "x1gh5o4n",
        $$css: true
    },
    navLinkSpacingRtl: {
        km5ZXQ: "x1rmmofs",
        $$css: true
    },
    linkActive: {
        kSiTet: "x1hc1fzr",
        $$css: true
    },
    linkHoverArea: {
        kSiTet: "x1hc1fzr",
        k5JduY: "x1s928wv",
        kkgrvl: "xe31u2d",
        kwXMNM: "x1j6awrg",
        k3foIR: "x1m1drc7",
        kH8cDV: "xnk186l",
        k4zj60: "x16fc1cd",
        $$css: true
    },
    linkButtonDrawer: {
        k1xSpc: "x78zum5",
        kGNEyG: "x6s0dn4",
        kOIVth: "x167g77z",
        $$css: true
    }
};
export function Header() {
    const [{ themeDrawer }, dispatch] = useAppReducer();
    function closeDrawer() {
        dispatch({
            type: "themeDrawer.close"
        });
    }
    const isRTL = useRTL();
    const router = useRouter();
    const alias = pipe(aliasFromRouter(router), RAlias.decode, E.fold(()=>router.pathname.startsWith("/focus/") ? "focusPosts" : "home", (alias)=>alias));
    return <div {...stylex.props(layout.gridCenteredMaxWidth, stylexStyles.root)} data-skip-search>
      <div className="xmmwo5t x1yedbjw x78zum5 x6s0dn4 x1qughib x1a02dak x2yie3v">
        <Link href="/" className="x1lliihq x25t5g8 x784prv x9v5kkp xtj0x4i x7s97pk" onClick={closeDrawer} onKeyDown={closeDrawer} role="button" tabIndex={0}>
          <LogoIO size="small"/>
        </Link>
        <div className="x78zum5 xdt5ytf x1qafhyn x1qughib xuk3077 xztvwtv">
          <nav {...stylex.props(typography.meta, stylexStyles.locales)}>
            {locales.map(({ lang, name }, i)=><LocaleLink key={lang} locale={lang} isFirst={i === 0}>
                {name}
              </LocaleLink>)}
          </nav>
          <nav className="x78zum5">
            <Link href="/" {...stylex.props(typography.heading3, stylexStyles.link, router.pathname === "/" && stylexStyles.linkActive)} onClick={closeDrawer} onKeyDown={closeDrawer} role="button" tabIndex={0}>
              <FormattedMessage id="Home" defaultMessage="Home"/>
            </Link>

            <button onClick={(e)=>{
        e.stopPropagation();
        dispatch({
            type: "themeDrawer.toggle"
        });
    }} aria-expanded={themeDrawer.isOpen} aria-haspopup="true" aria-owns="article-switcher" id="article-switcher-toggle" {...stylex.props(stylexStyles.linkButton, isRTL && stylexStyles.linkButtonRtl, typography.heading3, stylexStyles.link, stylexStyles.linkButtonDrawer, O.isSome(findArticleAlias(alias)) && stylexStyles.linkActive, themeDrawer.isOpen && stylexStyles.linkHoverArea)}>
              <FormattedMessage id="Themes" defaultMessage="Themes"/>
              <div {...{
        0: {
            className: "x1joubo7 x19jd1h0"
        },
        1: {
            className: "x1joubo7 x7p49u4"
        }
    }[!!themeDrawer.isOpen << 0]}>
                <ArrowTop16/>
              </div>
            </button>
            <Link href="/focus-articles" {...stylex.props(typography.heading3, stylexStyles.link, isRTL ? stylexStyles.navLinkSpacingRtl : stylexStyles.navLinkSpacingLtr, alias === "focusPosts" && stylexStyles.linkActive)} onClick={closeDrawer} onKeyDown={closeDrawer} role="button" tabIndex={0}>
              <FormattedMessage id="Focuses" defaultMessage="Focuses"/>
            </Link>
            <Link href="/indicators" {...stylex.props(typography.heading3, stylexStyles.link, isRTL ? stylexStyles.navLinkSpacingRtl : stylexStyles.navLinkSpacingLtr, alias === "indicators" && stylexStyles.linkActive)} onClick={closeDrawer} onKeyDown={closeDrawer} role="button" tabIndex={0}>
              <FormattedMessage id="SDG 4 indicators" defaultMessage="SDG 4 indicators"/>
            </Link>
            <Link href="/about" {...stylex.props(typography.heading3, stylexStyles.link, isRTL ? stylexStyles.navLinkSpacingRtl : stylexStyles.navLinkSpacingLtr, alias === "about" && stylexStyles.linkActive)} onClick={closeDrawer} onKeyDown={closeDrawer} role="button" tabIndex={0}>
              <FormattedMessage id="About" defaultMessage="About"/>
            </Link>
          </nav>
        </div>
      </div>
      <div className="x1hdmf5y">{router.pathname !== "/" ? <SecondaryBrandsBanner/> : null}</div>
    </div>;
}
